/**
 * frontend/src/components/Workspace/WorkspaceModal.js
 * 
 * A stylized modal for workspace creation.
 * Presented to new users or those without a workspace to help them get started.
 * Includes a branded design with animations using Framer Motion.
 * 
 * Common functions:
 * - handleSubmit: Submits the workspace name to the parent component for creation.
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiBriefcase, FiArrowRight, FiCheck } from 'react-icons/fi';

const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(15, 23, 42, 0.8)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px',
  },
  modal: {
    background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e1b4b 100%)',
    borderRadius: '24px',
    padding: '48px',
    maxWidth: '480px',
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
  },
  glow: {
    position: 'absolute',
    top: '-50%',
    right: '-50%',
    width: '100%',
    height: '100%',
    background: 'radial-gradient(circle, rgba(139, 92, 246, 0.3) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  glow2: {
    position: 'absolute',
    bottom: '-30%',
    left: '-30%',
    width: '80%',
    height: '80%',
    background: 'radial-gradient(circle, rgba(99, 102, 241, 0.2) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  content: {
    position: 'relative',
    zIndex: 1,
  },
  iconWrapper: {
    width: '72px',
    height: '72px',
    borderRadius: '20px',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
    boxShadow: '0 10px 40px -10px rgba(99, 102, 241, 0.5)',
  },
  icon: {
    fontSize: '32px',
    color: 'white',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: 'white',
    textAlign: 'center',
    marginBottom: '12px',
  },
  subtitle: {
    fontSize: '16px',
    color: 'rgba(255, 255, 255, 0.7)',
    textAlign: 'center',
    marginBottom: '32px',
    lineHeight: '1.6',
  },
  inputWrapper: {
    position: 'relative',
    marginBottom: '24px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: 'rgba(255, 255, 255, 0.9)',
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '16px 20px',
    fontSize: '16px',
    border: '2px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    background: 'rgba(255, 255, 255, 0.05)',
    color: 'white',
    outline: 'none',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box',
  },
  inputFocused: {
    borderColor: '#6366f1',
    background: 'rgba(255, 255, 255, 0.1)',
  },
  button: {
    width: '100%',
    padding: '16px 24px',
    fontSize: '16px',
    fontWeight: '600',
    border: 'none',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    transition: 'all 0.2s ease',
    boxShadow: '0 10px 40px -10px rgba(99, 102, 241, 0.5)',
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  buttonLoading: {
    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
  },
  hint: {
    fontSize: '13px',
    color: 'rgba(255, 255, 255, 0.5)',
    textAlign: 'center',
    marginTop: '20px',
  },
};

const WorkspaceModal = ({ show, onSubmit, loading = false, userName }) => {
  const [workspaceName, setWorkspaceName] = useState('');
  const [inputFocused, setInputFocused] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (workspaceName.trim() && !loading) {
      onSubmit(workspaceName.trim());
    }
  };

  const isValid = workspaceName.trim().length > 0;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          style={modalStyles.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            style={modalStyles.modal}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            <div style={modalStyles.glow} />
            <div style={modalStyles.glow2} />

            <div style={modalStyles.content}>
              <motion.div
                style={modalStyles.iconWrapper}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1, type: 'spring', damping: 15 }}
              >
                <FiBriefcase style={modalStyles.icon} />
              </motion.div>

              <motion.h2
                style={modalStyles.title}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
              >
                Welcome{userName ? `, ${userName.split(' ')[0]}` : ''}!
              </motion.h2>

              <motion.p
                style={modalStyles.subtitle}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                Create a workspace to start managing your change requests.
                This will be your team's home.
              </motion.p>

              <motion.form
                onSubmit={handleSubmit}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
              >
                <div style={modalStyles.inputWrapper}>
                  <label style={modalStyles.label}>Workspace Name</label>
                  <input
                    type="text"
                    placeholder="e.g., My Company, Side Project"
                    value={workspaceName}
                    onChange={(e) => setWorkspaceName(e.target.value)}
                    onFocus={() => setInputFocused(true)}
                    onBlur={() => setInputFocused(false)}
                    style={{
                      ...modalStyles.input,
                      ...(inputFocused ? modalStyles.inputFocused : {}),
                    }}
                    autoFocus
                    disabled={loading}
                  />
                </div>

                <motion.button
                  type="submit"
                  style={{
                    ...modalStyles.button,
                    ...(!isValid || loading ? modalStyles.buttonDisabled : {}),
                    ...(loading ? modalStyles.buttonLoading : {}),
                  }}
                  disabled={!isValid || loading}
                  whileHover={isValid && !loading ? { scale: 1.02 } : {}}
                  whileTap={isValid && !loading ? { scale: 0.98 } : {}}
                >
                  {loading ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        style={{ width: 20, height: 20, border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%' }}
                      />
                      Creating...
                    </>
                  ) : (
                    <>
                      Get Started
                      <FiArrowRight />
                    </>
                  )}
                </motion.button>
              </motion.form>

              <motion.p
                style={modalStyles.hint}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                You can invite team members later from settings
              </motion.p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WorkspaceModal;
